'use client';

import React, { useCallback, useState, useMemo, Suspense, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { InstantSearchNext } from 'react-instantsearch-nextjs';
import { Configure } from 'react-instantsearch';
import { searchClient } from 'components/essentials/AlgoliaSearchClient';
import { ALGOLIA_INDEX_NAME, NUMBER_OF_PRODUCTS_TO_SHOW, NUMBER_OF_SWATCHES_TO_SHOW, EMPTY_SHOWROOM } from 'commons/constants';
import isClient from 'commons/isClient';
import AdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/AdvancedSearchContext';
import { useTransition } from 'components/consumer/consumer-search/advanced-search-next/hooks/reactShims';
import addDerivedProductData from 'hooks/useProductListingData/helpers/addDerivedProductData';
import getLowestTierVariantFirst from 'hooks/useProductListingData/helpers/getLowestTierVariantFirst';
import ShowroomDisplayContext from './hooks/ShowroomDisplayContext';
import CategoryAlgoliaLayout from './components/CategoryAlgoliaLayout';
import TriggerInitialSearch from './components/TriggerInitialSearch';
import ShowroomDataLoader from './components/ShowroomDataLoader';
import CategoryPageColorRestoration from './components/CategoryPageColorRestoration';
import categoryPageAlgoliaInstance from './categoryPageAlgoliaInstance';
import useCategoryPageURL from './hooks/useCategoryPageURL';
import buildCategoryAlgoliaConfigureFilters from './buildCategoryAlgoliaConfigureFilters';
import { getSortByFromProductIndex, parseCategoryPageUrlState, serializeFilterState } from './categoryPageUrlState';
const applyParsedUrlState = (parsed, setters) => {
  setters.setCurrentProductIndex(parsed.currentProductIndex);
  setters.setIsReadyToShipActive(parsed.isReadyToShipActive);
  setters.setMinWidth(parsed.minWidth);
  setters.setMaxWidth(parsed.maxWidth);
  setters.setSelectedColors(parsed.selectedColors);
  setters.setSearchQuery(parsed.searchQuery);
  setters.setSelectedFacets(parsed.selectedFacets);
};
const CategoryPageAlgolia = ({
  slug,
  serverCategoryData
}) => {
  const filters = useMemo(() => serverCategoryData?.getCategoryPage?.filters ?? [], [serverCategoryData?.getCategoryPage?.filters]);
  const {
    pagesUrlParam,
    parsedUrlState,
    updateUrlParamImmediate,
    syncFilterStateToUrl
  } = useCategoryPageURL(slug, filters);
  const [currentProductIndex, setCurrentProductIndex] = useState(parsedUrlState.currentProductIndex);
  const [isReadyToShipActive, setIsReadyToShipActive] = useState(parsedUrlState.isReadyToShipActive);
  const [minWidth, setMinWidth] = useState(parsedUrlState.minWidth);
  const [maxWidth, setMaxWidth] = useState(parsedUrlState.maxWidth);
  const [selectedColors, setSelectedColors] = useState(parsedUrlState.selectedColors);
  const [searchQuery, setSearchQuery] = useState(parsedUrlState.searchQuery);
  const [activeTypeFilter, setActiveTypeFilter] = useState(null);
  const [selectedFacets, setSelectedFacets] = useState(parsedUrlState.selectedFacets);
  const [colorIdsFromUrl, setColorIdsFromUrl] = useState(parsedUrlState.colorIds);
  const [, startTransition] = useTransition();
  const [showroomData, setShowroomData] = useState(EMPTY_SHOWROOM);
  const filterStateRef = useRef({});
  filterStateRef.current = {
    sortBy: getSortByFromProductIndex(currentProductIndex),
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    selectedColors,
    selectedFacets
  };
  const filterSignatureRef = useRef(serializeFilterState(filterStateRef.current));
  const syncToUrl = useCallback((partialState, {
    resetPages = false
  } = {}) => {
    const nextState = {
      ...filterStateRef.current,
      ...partialState
    };
    const nextSignature = serializeFilterState(nextState);
    if (nextSignature === filterSignatureRef.current) return;
    filterSignatureRef.current = nextSignature;
    syncFilterStateToUrl(nextState, {
      resetPages
    });
  }, [syncFilterStateToUrl]);
  const stateSetters = useMemo(() => ({
    setCurrentProductIndex,
    setIsReadyToShipActive,
    setMinWidth,
    setMaxWidth,
    setSelectedColors,
    setSearchQuery,
    setSelectedFacets
  }), []);
  useEffect(() => {
    if (!isClient()) return undefined;
    const handlePopState = () => {
      const parsed = parseCategoryPageUrlState(new URLSearchParams(window.location.search), filters);
      const nextSignature = serializeFilterState({
        sortBy: parsed.sortBy,
        isReadyToShipActive: parsed.isReadyToShipActive,
        minWidth: parsed.minWidth,
        maxWidth: parsed.maxWidth,
        searchQuery: parsed.searchQuery,
        selectedColors: parsed.selectedColors,
        selectedFacets: parsed.selectedFacets
      });
      if (nextSignature === filterSignatureRef.current) return;
      filterSignatureRef.current = nextSignature;
      applyParsedUrlState(parsed, stateSetters);
      setColorIdsFromUrl(parsed.colorIds);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [filters, stateSetters]);
  const updateCurrentProductIndex = useCallback(indexName => {
    startTransition(() => {
      setCurrentProductIndex(indexName);
    });
    syncToUrl({
      sortBy: getSortByFromProductIndex(indexName)
    }, {
      resetPages: true
    });
  }, [startTransition, syncToUrl]);
  const handleToggleReadyToShip = useCallback(checked => {
    setIsReadyToShipActive(checked);
    syncToUrl({
      isReadyToShipActive: checked
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const handleWidthChange = useCallback((min, max) => {
    setMinWidth(min);
    setMaxWidth(max);
    syncToUrl({
      minWidth: min,
      maxWidth: max
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const selectColor = useCallback(colorLabel => {
    const nextSelectedColors = {
      ...filterStateRef.current.selectedColors,
      [colorLabel]: !filterStateRef.current.selectedColors?.[colorLabel]
    };
    setSelectedColors(nextSelectedColors);
    syncToUrl({
      selectedColors: nextSelectedColors
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const clearSelectedColors = useCallback(() => {
    setSelectedColors({});
    syncToUrl({
      selectedColors: {}
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const handleToggleFacet = useCallback((attribute, value) => {
    const prev = filterStateRef.current.selectedFacets;
    const current = prev[attribute] || new Set();
    const next = new Set(current);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    const result = {
      ...prev
    };
    if (next.size === 0) {
      delete result[attribute];
    } else {
      result[attribute] = next;
    }
    setSelectedFacets(result);
    syncToUrl({
      selectedFacets: result
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const clearSelectedFacets = useCallback(() => {
    setSelectedFacets({});
    syncToUrl({
      selectedFacets: {}
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const handleSearchQueryChange = useCallback(value => {
    setSearchQuery(value);
    syncToUrl({
      searchQuery: value
    }, {
      resetPages: true
    });
  }, [syncToUrl]);
  const context = useMemo(() => ({
    currentSearchSection: ALGOLIA_INDEX_NAME.PRODUCTS,
    currentProductIndex,
    updateSearchSection: () => {},
    updateCurrentProductIndex,
    isReadyToShipActive,
    handleToggleReadyToShip,
    minWidth,
    maxWidth,
    handleWidthChange,
    selectedColors,
    selectColor,
    clearSelectedColors,
    pagesUrlParam,
    activeTypeFilter,
    setActiveTypeFilter,
    updateUrlParamImmediate,
    selectedFacets,
    clearSelectedFacets,
    searchQuery,
    onSearchQueryChange: handleSearchQueryChange,
    syncFilterStateToUrl
  }), [currentProductIndex, updateCurrentProductIndex, isReadyToShipActive, handleToggleReadyToShip, minWidth, maxWidth, handleWidthChange, selectedColors, selectColor, clearSelectedColors, pagesUrlParam, activeTypeFilter, updateUrlParamImmediate, selectedFacets, clearSelectedFacets, searchQuery, handleSearchQueryChange, syncFilterStateToUrl]);
  const configureFilters = useMemo(() => buildCategoryAlgoliaConfigureFilters({
    slug,
    isReadyToShipActive,
    selectedFacets
  }), [slug, isReadyToShipActive, selectedFacets]);
  const initialProducts = useMemo(() => {
    const category = serverCategoryData?.getCategoryPage;
    if (!category?.products?.length) return [];
    const upholsteryOptions = category.upholsteryOptions || [];
    return category.products.map((product, index) => {
      if (!(product?.variants?.length > 0)) return null;
      const derived = addDerivedProductData({
        product,
        upholsteryOptions
      });
      const orderedVariants = getLowestTierVariantFirst(derived.variants, derived.id);
      const variantsToShow = (orderedVariants?.length > 0 ? orderedVariants : derived.variants).slice(0, NUMBER_OF_SWATCHES_TO_SHOW);
      return {
        ...derived,
        variantsToShow,
        moreVariantsCount: variantsToShow.length ? (derived.variantsTotal ?? derived.variants.length) - variantsToShow.length : 0,
        position: index + 1
      };
    });
  }, [serverCategoryData]);
  const numericFilters = useMemo(() => {
    const filterList = ['item_availability=0', 'visibility>1', 'visibility<4'];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filterList.push(`item_width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filterList.push(`item_width<=${maxNum}`);
    }
    return filterList;
  }, [minWidth, maxWidth]);
  const initialUiState = useMemo(() => ({
    [parsedUrlState.currentProductIndex]: {
      query: parsedUrlState.searchQuery || '',
      ...(parsedUrlState.colorIds.length > 0 ? {
        refinementList: {
          option_color: parsedUrlState.colorIds
        }
      } : {})
    }
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return <AdvancedSearchContext.Provider value={context}>
      <ShowroomDisplayContext.Provider value={showroomData}>
        <InstantSearchNext key={slug} searchClient={searchClient} indexName={currentProductIndex} instance={categoryPageAlgoliaInstance} initialUiState={initialUiState} future={{
        preserveSharedStateOnUnmount: false
      }}>
          <Configure facets={['*']} filters={configureFilters} numericFilters={numericFilters} hitsPerPage={NUMBER_OF_PRODUCTS_TO_SHOW} attributesToRetrieve={['_highlightResult', 'product_id', 'name', 'is_in_stock']} clickAnalytics />
          <TriggerInitialSearch />
          <CategoryPageColorRestoration colorIds={colorIdsFromUrl} />
          <Suspense fallback={null}>
            <ShowroomDataLoader onLoad={setShowroomData} />
          </Suspense>

          <CategoryAlgoliaLayout key={slug} slug={slug} serverCategoryData={serverCategoryData} initialProducts={initialProducts} configureFilters={configureFilters} selectedFacets={selectedFacets} onToggleFacet={handleToggleFacet} onClearFacets={clearSelectedFacets} />
        </InstantSearchNext>
      </ShowroomDisplayContext.Provider>
    </AdvancedSearchContext.Provider>;
};
CategoryPageAlgolia.propTypes = {
  slug: PropTypes.string.isRequired,
  serverCategoryData: PropTypes.object
};
export default CategoryPageAlgolia;